'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Trash2, CircleDot, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import type { StorageLocation } from '../types';

interface StorageLocationCardProps {
  location: StorageLocation;
  onEdit: (item: StorageLocation) => void;
  onDelete: (item: StorageLocation) => void;
}

const STATUS_STYLES: Record<string, string> = {
  active:
    'border-green-500/30 bg-green-500/10 text-green-600 dark:text-green-400',
  full: 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400'
};

const getCodeColor = (code: string) => {
  const colors = [
    'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
    'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
    'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
    'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
    'bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-300',
    'bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300'
  ];
  const hash = code.charCodeAt(0) + (code.charCodeAt(1) || 0);
  return colors[hash % colors.length];
};

const getUtilizationColor = (utilization: number) => {
  if (utilization < 80) return 'bg-blue-500';
  if (utilization < 90) return 'bg-amber-500';
  return 'bg-red-500';
};

export function StorageLocationCard({
  location,
  onEdit,
  onDelete
}: StorageLocationCardProps) {
  const t = useTranslations('storageLocations');

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        {/* Top: name + status */}
        <div className='mb-4 flex items-start justify-between'>
          <div className='min-w-0 flex-1'>
            <h3 className='truncate font-semibold'>{location.name}</h3>
            <div
              className={cn(
                'mt-1 inline-block rounded px-2 py-0.5 font-mono text-xs font-bold',
                getCodeColor(location.code)
              )}
            >
              {location.code}
            </div>
          </div>
          <Badge
            variant='outline'
            className={cn(
              'ms-2 shrink-0 text-xs uppercase',
              STATUS_STYLES[location.status]
            )}
          >
            <CircleDot className='me-1 h-3 w-3 fill-current' />
            {location.status === 'active' ? t('statusActive') : t('statusFull')}
          </Badge>
        </div>

        {/* Middle: utilization + capacity */}
        <div className='mb-4 space-y-3'>
          <div className='space-y-1'>
            <div className='flex items-center justify-between text-sm'>
              <span className='text-muted-foreground'>
                {t('columns.utilization')}
              </span>
              <span className='font-medium'>{location.utilization}%</span>
            </div>
            <Progress
              value={location.utilization}
              className={cn('h-2', getUtilizationColor(location.utilization))}
            />
          </div>

          <div className='text-muted-foreground flex items-center justify-between text-sm'>
            <span>{t('columns.shelves')}</span>
            <span className='font-medium'>
              {t('usedOfCapacity', {
                used: location.used_shelves,
                capacity: location.capacity
              })}
            </span>
          </div>

          {location.parent && (
            <div className='text-muted-foreground flex items-center gap-1.5 text-sm'>
              <MapPin className='h-3.5 w-3.5 shrink-0' />
              <span className='truncate'>{location.parent.name}</span>
            </div>
          )}

          {location.breadcrumb && (
            <p className='text-muted-foreground truncate text-xs uppercase'>
              {location.breadcrumb}
            </p>
          )}
        </div>

        {/* Bottom: actions */}
        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <PermissionGate permissions={['storage_locations.edit']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onEdit(location)}
            >
              <Pencil className='h-4 w-4' />
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['storage_locations.delete']}>
            <Button
              variant='ghost'
              size='icon'
              className='text-destructive h-8 w-8'
              onClick={() => onDelete(location)}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </PermissionGate>
        </div>
      </CardContent>
    </Card>
  );
}
